import { useState } from "react";
import { Plus } from "lucide-react";

import {
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  DataTable,
  contentItems,
  contentTableConfig,
  FiltersBar,
  FilterConfig,
} from "@b2b/ui-components";

const AdminContent = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [activeTab, setActiveTab] = useState("All");
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();

  // Global filtering logic
  const filteredItems = contentItems
    .filter((item) => {
      const title = item.title.toLowerCase();
      const matchesSearch =
        normalizedSearchTerm.length === 0 || title.includes(normalizedSearchTerm);
      const matchesType = filterType === "All" || item.type === filterType;
      const matchesStatus =
        filterStatus === "All" || item.status === filterStatus;
      return matchesSearch && matchesType && matchesStatus;
    })
    .sort((a, b) => {
      if (!normalizedSearchTerm) return 0;

      const aTitle = a.title.toLowerCase();
      const bTitle = b.title.toLowerCase();

      const getRank = (title: string) => {
        if (title === normalizedSearchTerm) return 0;
        if (title.startsWith(normalizedSearchTerm)) return 1;
        return 2;
      };

      return getRank(aTitle) - getRank(bTitle);
    });

  // Filter further by active tab
  const displayedItems =
    activeTab === "All"
      ? filteredItems
      : filteredItems.filter((item) => item.type === activeTab);

  const filters: FilterConfig[] = [
    {
      type: "select",
      placeholder: "Content Type",
      value: filterType,
      options: [
        { label: "All Types", value: "All" },
        { label: "Banner", value: "Banner" },
        { label: "Blog", value: "Blog" },
        { label: "Product Guide", value: "Product Guide" },
        { label: "FAQ", value: "Faq" },
      ],
      onChange: setFilterType,
    },
    {
      type: "select",
      placeholder: "Status",
      value: filterStatus,
      options: [
        { label: "All Status", value: "All" },
        { label: "Published", value: "published" },
        { label: "Draft", value: "draft" },
        { label: "Archived", value: "archived" },
      ],
      onChange: setFilterStatus,
    },
  ];

  return (
    <div className="space-y-8 mt-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-foreground">
            Content Management
          </h1>
          <p className="text-muted-foreground">
            Manage banners, blogs, product guides, and FAQs for your marketplace
          </p>
        </div>
        <Button variant="primary">
          <Plus className="h-4 w-4 mr-2" />
          Add Content
        </Button>
      </div>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-6"
      >
        <TabsList className="grid w-full grid-cols-5 bg-gray-100 rounded-md overflow-hidden">
          {["All", "Banner", "Blog", "Product Guide", "Faq"].map((tab) => {
            const labelMap: Record<string, string> = {
              All: "All Content",
              Banner: "Banners",
              Blog: "Blogs",
              "Product Guide": "Product Guides",
              Faq: "FAQs",
            };

            return (
              <TabsTrigger
                key={tab}
                value={tab}
                className={`text-white font-medium ${
                  activeTab === tab
                    ? "bg-orange-500"
                    : "bg-gray-100 text-gray-700"
                }  text-center transition-colors duration-200`}
              >
                {labelMap[tab]}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* Filters */}
        <FiltersBar
          filters={filters}
          card
          showSearch
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
        />

        {/* Table */}
        <TabsContent value={activeTab}>
          <DataTable data={displayedItems} config={contentTableConfig} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminContent;
